import { BodyContainer, SubTitle } from '@features/siteStatusV2/components/display-form';
import { ReportStatus } from '@features/siteStatusV2/constants';
import { loadReports } from '@features/siteStatusV2/reducer/reportSlice';
import { sortObjectsByDate } from '@features/siteStatusV2/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';

import { useAppDispatch } from '../../../../app/hooks';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';
import { createComplaintModalConfig, createFailureModalConfig } from './helpers';
import { ReportModal } from './modal';
import ReportElement from './ReportElement';
import ReportElementSkeleton from './ReportElementSkeleton';
import { ReportModalConfig } from './types/commun';

interface ReportsProps {
  refPrestationPrise: string;
}

const Reports: React.FC<ReportsProps> = ({ refPrestationPrise }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const dispatch = useAppDispatch();
  const [modalConfig, setModalConfig] = useState<ReportModalConfig | null>(null);
  const { isReportLoading, complaintReports, failureReports } = useSelector(
    (state: RootState) => state.report
  );
  const failureReportsSort = useMemo(
    () => sortObjectsByDate(failureReports ?? [], 'creationDate', 'desc'),
    [isReportLoading]
  );

  const complaintReportsSort = useMemo(
    () => sortObjectsByDate(complaintReports ?? [], 'creationDate', 'desc'),
    [isReportLoading]
  );

  useEffect(() => {
    dispatch(loadReports({ refPrestationPrise }));
  }, [refPrestationPrise]);

  const handleSeeComplaint = (id: string) => {
    const complaintReport = complaintReportsSort.find((report) => report.ticketNumber === id);
    let complaintModalConfig: ReportModalConfig | null = null;
    if (complaintReport) {
      complaintModalConfig = createComplaintModalConfig(complaintReport);
    }
    setModalConfig(complaintModalConfig);
  };

  const handleSeeFailure = (id: string) => {
    const failureReport = failureReportsSort.find((report) => report.id === id);
    let failureModalConfig: ReportModalConfig | null = null;
    if (failureReport !== undefined) {
      failureModalConfig = createFailureModalConfig(failureReport);
    }
    setModalConfig(failureModalConfig);
  };

  if (isReportLoading) {
    return (
      <BodyContainer>
        <SubTitle color="orange" display={t('report.title')} />
        <ReportElementSkeleton />
        <ReportElementSkeleton />
        <ReportElementSkeleton />
        <ReportElementSkeleton />
        <ReportElementSkeleton />
      </BodyContainer>
    );
  }

  if (complaintReportsSort?.length !== 0 || failureReportsSort?.length !== 0) {
    return (
      <>
        <BodyContainer>
          <SubTitle color="orange" display={t('report.title')} />
          {complaintReportsSort?.map((complaint) => (
            <ReportElement
              key={complaint.ticketNumber}
              id={complaint.ticketNumber}
              date={complaint.creationDate}
              type={t('report.complaint')}
              status={complaint.status}
              rawStatus={complaint.rawStatus}
              handleSeeButtonClick={handleSeeComplaint}
            />
          ))}
          {failureReportsSort?.map((failure) => (
            <ReportElement
              key={failure.id}
              id={failure.id}
              date={failure.creationDate}
              type={t('report.failure')}
              status={failure.status}
              rawStatus={failure.rawStatus}
              handleSeeButtonClick={handleSeeFailure}
            />
          ))}
        </BodyContainer>
        <ReportModal
          showReportModal={modalConfig !== null}
          onHide={() => {
            setModalConfig(null);
          }}
          report={
            modalConfig ?? {
              header: {
                title: '',
                ticketId: '',
                status: ReportStatus.NO_STATUS,
                rawStatus: '',
                creationDate: ''
              },
              sections: []
            }
          }
        />
      </>
    );
  }

  return null;
};

export default Reports;
